import React, { useState, createContext, useContext, useRef } from "react"
import { navigate } from "gatsby"
import produce from "immer"
import InitialGuarantorFormState from "./initial-guarantor-form-state"
import InitialBorrowerFormState from "./initial-borrower-form-state"
import { REST } from "./api-calls-map"

const isBrowser = typeof window !== "undefined"

const ApplyContext = createContext({})

export const useApplyContext = () => useContext(ApplyContext)

export const ApplyProvider = ({ children }) => {
  const isGuarantor = isBrowser && window.location.href.includes("guarantor")

  const domain =
    isBrowser &&
    window &&
    window.location &&
    window.location.origin &&
    window.location.origin &&
    window.location.origin === "http://local.dev.trustic.com:8000"
      ? "http://dev.trustic.com"
      : isBrowser && window.location.origin

  const API_MAP = isBrowser && {
    checkState: `${domain}/origination/api/public/checkState`,
    getAgreement: `${domain}/origination/api/public/agreement`,
    login: `${domain}/origination/api/auth/login`,
    authenticateUserWithGoogle: `${domain}/origination/api/auth/login/google`,
    initLoanApplication: `${domain}/origination/api/public/init/loanApplication`,
    borrowerAuth: `${domain}/origination/api/register/borrower`,
    loanApplication: `${domain}/origination/api/loanApplication/${localStorage.getItem(
      "businessId"
    )}`,
    updateLoanApplicationState: `${domain}/origination/api/loanApplication/state/${localStorage.getItem(
      "businessId"
    )}`,
    initiateNewLoanApplication: `${domain}/origination/api/loanApplication`,
    getLoanApplication: `${domain}/origination/api/loanApplication`,
    getLoanApplications: `${domain}/origination/api/loanApplication/list`,
    acceptLoanOffer: `${domain}/origination/api/loanApplication/${localStorage.getItem(
      "businessId"
    )}/loanOffer`,
    updateLivingSituation: `${domain}/origination/api/person/livingSituation`,
    getAllIncomeSources: `${domain}/origination/api/person/incomeSource`,
    addIncomeSource: `${domain}/origination/api/person/incomeSource`,
    deleteIncomeSource: `${domain}/origination/api/person/incomeSource`,
    getIncomeSourceTypes: `${domain}/origination/api/person/incomeSourceTypes`,
    addRepaymentMethod: `${domain}/origination/api/person/repaymentMetod`,
    getRepaymentTypes: `${domain}/origination/api/person/repaymentTypes`,
    updateBankAccount: `${domain}/origination/api/person/bankAccount`,
    addDisbursementAccount: `${domain}/origination/api/loanApplication/${localStorage.getItem(
      "businessId"
    )}/disbursementAccount`,
    updateRepaymentMethod: `${domain}/origination/api/person/repaymentMethod`,
    makePayment: `${domain}/origination/api/payment`,
    makePublicPayment: `${domain}/origination/api/public/payment`,
    getPaymentLink: `${domain}/origination/api/public/payment`,
    getPaymentDetails: `${domain}/origination/api/payment/paymentDetails`,
    getTodoItems: `${domain}/origination/api/person/todoItems`,
    signContract: `${domain}/origination/api/loanApplication/contract`,
    getLoanApplicationBorrowerContract: `${domain}/origination/api/loanApplication/contract/${localStorage.getItem(
      "businessId"
    )}`,
    simulateOffer: `${domain}/origination/api/loanApplication/${localStorage.getItem(
      "businessId"
    )}/simulate/loanOffer`,
    newSimulateOffer: `${domain}/origination/api/loanApplication/${localStorage.getItem(
      "businessId"
    )}/simulate/loanOffer`,
    initGuarantorApplication: `${domain}/origination/api/public/init/guarantorApplication`,
    getGuarantorBorrowerDetails: `${domain}/origination/api/public/guarantor/invite`,
    registerAndInitiateGuarantorApplication: `${domain}/origination/api/register/guarantor`,
    registerAndInitiateLoanApplicationWithGoogle: `${domain}/origination/api/register/google/borrower`,
    registerAndInitiateGuarantorApplicationWithGoogle: `${domain}/origination/api/register/google/guarantor`,
    getGuarantorApplication: `${domain}/origination/api/guarantorApplication/${localStorage.getItem(
      "businessId"
    )}`,
    getGuarantorApplications: `${domain}/origination/api/guarantorApplication/list`,
    updateGuarantorApplication: `${domain}/origination/api/guarantorApplication/${localStorage.getItem(
      "businessId"
    )}`,
    updateGuarantorApplicationState: `${domain}/origination/api/guarantorApplication/state/${localStorage.getItem(
      "businessId"
    )}`,
    signBorrowerContract: `${domain}/origination/api/guarantorApplication/borrowerAgreement/${localStorage.getItem(
      "businessId"
    )}/sign`,
    getBorrowerContract: `${domain}/origination/api/guarantorApplication/borrowerAgreement/${localStorage.getItem(
      "businessId"
    )}`,
    getLoanApplicationGuarantorContract: `${domain}/origination/api/guarantorApplication/contract/${localStorage.getItem(
      "businessId"
    )}`,
    signGuarantorContract: `${domain}/origination/api/guarantorApplication/contract`,
    getPageBorrowerContract: `${domain}/origination/api/pages/contract/borrower/${localStorage.getItem(
      "businessId"
    )}`,
    getPageBorrowerContractGuarantor: `${domain}/origination/api/pages/contract/borrower`,
    getPageGuarantorContract: `${domain}/origination/api/pages/contract/guarantor/${localStorage.getItem(
      "businessId"
    )}`,
    downloadApplicationGuarantorContract: `${domain}/origination/api/guarantorApplication/contract/download/${localStorage.getItem(
      "businessId"
    )}`,
    downloadApplicationBorrowerContract: `${domain}/origination/api/loanApplication/contract/download/${localStorage.getItem(
      "businessId"
    )}`,
    downloadApplicationBorrowerContractGuarantor: `${domain}/origination/api/guarantorApplication/contract/borrower/download`,
    resetPassword: `${domain}/origination/api/auth/password/reset`,
    plaidLinkToken: `${domain}/origination/api/plaid/linkToken`,
    plaidLinkTokenUpdate: `${domain}/origination/api/plaid/linkToken/update`,
    publicTokenSave: `${domain}/origination/api/plaid/publicToken`,
    publicTokenUpdate: `${domain}/origination/api/plaid/publicToken/update`,
    publicPaymentPlaidLinkToken: `${domain}/origination/api/public/payment/plaidLink`,
    publicPaymentPlaidLinkTokenUpdate: `${domain}/origination/api/public/payment/plaid/bankAccounts`,
    uploadFiles: `${domain}/origination/api/person/upload`,
    makePlaidPayment: `${domain}/origination/api/public/payment/plaid/payment`,
    sendPlaidEvent: `${domain}/origination/api/plaid/event`,
  }

  const loanStatusInterval = useRef()

  const [appLoading, setAppLoading] = useState(true)

  const [showTermsModal, setShowTermsModal] = useState(false)

  const [termsModalContent, setTermsModalContent] = useState(null)

  const [appLoadingStatus, setAppLoadingStatus] = useState(true)

  const [appRejected, setAppRejected] = useState(false)

  const [stepLoading, setStepLoading] = useState(false)

  const [activeStep, setActiveStep] = useState(0)

  const [isNotFirstApplication, setIsNotFirstApplication] = useState(null)

  const [formState, setFormState] = useState(
    isGuarantor ? InitialGuarantorFormState : InitialBorrowerFormState
  )

  const [loanApplication, setLoanApplication] = useState(null)

  const [linkManually, setLinkManually] = useState(false)

  const [linkBankLoading, setLinkBankLoading] = useState(false)

  const [selectedPaymentType, setSelectedPaymentType] = useState(null)

  const [repaymentTypes, setRepaymentTypes] = useState([])

  const [toggleBankAccountUpdate, setToggleBankAccountUpdate] = useState(false)

  const [todoItems, setTodoItems] = useState([])

  const [loggedIn, setLoggedIn] = useState(false)

  const initiateNewLoanApplication = async values => {
    setAppLoading(true)
    setAppLoadingStatus("Initializing new loan")

    const [res, status] = await REST({
      url:
        API_MAP.initLoanApplication +
        `?purpose=${localStorage.getItem("moneyReason")}`,
      method: "POST",
    })

    return [res, status]
  }

  const getTodoItems = async () => {
    const [res, status] = await REST({
      url: API_MAP.getTodoItems,
      method: "GET",
    })

    if (status === 200) {
      setTodoItems(res)
    }

    if (status === 400 || status === 401 || status === 403) {
      return false
    }
  }

  const getRepaymentTypes = async () => {
    const [res, status] = await REST({
      url: API_MAP.getRepaymentTypes,
      method: "GET",
    })

    if (status === 200) {
      setRepaymentTypes(res)
    }
  }

  const addRepaymentMethod = async values => {
    setAppLoading(true)
    setAppLoadingStatus("Saving your payment method")
    const [res, status] = await REST({
      url: API_MAP.addRepaymentMethod,
      method: "POST",
      body: values,
    })

    if (
      (status === 200 &&
        values.repaymentMethodType.name === "DIRECT_DEBIT" &&
        res.active) ||
      (status === 200 &&
        values.repaymentMethodType.name !== "DIRECT_DEBIT" &&
        res.active)
    ) {
      submitStepAndUpdateAppState({})
    }
    setAppLoadingStatus(null)
    setAppLoading(false)

    return [res, status]
  }

  const updateBankAccount = async values => {
    setAppLoading(true)
    setAppLoadingStatus("Saving your payment method")
    const [res, status] = await REST({
      url: API_MAP.updateBankAccount,
      method: "POST",
      body: values,
    })

    if (status === 200 && res.active) {
      // window.history.back()
      navigate(-1)
    }

    setAppLoading(false)
    setAppLoadingStatus(null)

    return [res, status]
  }

  const updateRepaymentMethod = async values => {
    setAppLoading(true)
    setAppLoadingStatus("Saving your payment method")
    const [res, status] = await REST({
      url: API_MAP.updateRepaymentMethod,
      method: "POST",
      body: values,
    })

    if (status === 200 && res.active) {
      // window.history.back()
      navigate(-1)
    }

    setAppLoading(false)
    setAppLoadingStatus(null)

    return [res, status]
  }

  const makePayment = async values => {
    setAppLoading(true)
    setAppLoadingStatus("Processing Payment")
    const [res, status] = await REST({
      url: API_MAP.makePayment,
      method: "POST",
      body: values,
    })

    setAppLoading(false)
    setAppLoadingStatus(null)

    return [res, status]
  }

  const makePublicPayment = async (values, token) => {
    setAppLoading(true)
    setAppLoadingStatus("Processing Payment")
    const [res, status] = await REST({
      url: `${API_MAP.makePublicPayment}/${token}`,
      method: "POST",
      body: values,
    })

    setAppLoading(false)
    setAppLoadingStatus(null)

    return [res, status]
  }

  const makePlaidPayment = async (values, token) => {
    setAppLoading(true)
    setAppLoadingStatus("Processing Payment")
    const [res, status] = await REST({
      url: `${API_MAP.makePlaidPayment}/${token}`,
      method: "POST",
      body: values,
    })

    setAppLoading(false)
    setAppLoadingStatus(null)

    return [res, status]
  }

  const getPaymentLink = async token => {
    setAppLoading(true)
    setAppLoadingStatus("Loading Payment Link")
    const [res, status] = await REST({
      url: `${API_MAP.getPaymentLink}/${token}`,
      method: "GET",
    })

    setAppLoading(false)
    setAppLoadingStatus(null)

    return [res, status]
  }

  const getPaymentDetails = async () => {
    setAppLoading(true)
    setAppLoadingStatus("Loading Payment Link")
    const [res, status] = await REST({
      url: `${API_MAP.getPaymentDetails}`,
      method: "GET",
    })

    setAppLoading(false)
    setAppLoadingStatus(null)

    return [res, status]
  }

  const addDisbursementAccount = async values => {
    setAppLoading(true)
    setAppLoadingStatus("Saving your disbursement method")
    const [res, status] = await REST({
      url: API_MAP.addDisbursementAccount,
      method: "POST",
      body: values,
    })

    if (status === 200) {
      // window.history.back()
      navigate(-1)
    }

    setAppLoading(false)
    setAppLoadingStatus(null)

    return [res, status]
  }

  const checkLoanStatus = async skipSetActiveState => {
    setAppLoading(true)
    setAppLoadingStatus(
      isGuarantor
        ? "We are getting your decision..."
        : "We are getting you a loan offer..."
    )
    loanStatusInterval.current = setInterval(async () => {
      const [res, status] = await REST({
        url: isGuarantor
          ? API_MAP.getGuarantorApplication
          : API_MAP.loanApplication,
        method: "GET",
      })

      if (status === 200) {
        const applicationState = JSON.parse(res.applicationState)

        if (res.status === "CONDITIONAL_APPROVED") {
          setAppLoading(true)
          if (isGuarantor) {
            if (applicationState.activeStep === 6) {
              applicationState.state[6] = res
            }
          } else {
            if (applicationState.activeStep === 5) {
              applicationState.state[5] = res.loanOffers
            }
          }
          setFormState(applicationState.state)
          setActiveStep(applicationState.activeStep)
          setAppLoading(false)
          clearInterval(loanStatusInterval.current)
        }

        const Difference_In_Time =
          new Date().getTime() - new Date(res.lastStatusUpdate).getTime()

        const Difference_In_Days = Difference_In_Time / (1000 * 3600 * 24)

        /**
         * Send to summary if not guarantor and rejected or canceled
         */
        if (
          !isGuarantor &&
          (res.status === "REJECTED" ||
            res.status === "CANCELED" ||
            res.status === "EXPIRED" ||
            res.loanStatus === "PAID_OFF")
        ) {
          setActiveStep(9)
          clearInterval(loanStatusInterval.current)
        }

        if (
          isGuarantor &&
          ((res.status === "REJECTED" && Difference_In_Days < 30) ||
            (skipSetActiveState &&
              res.status === "REJECTED" &&
              Difference_In_Days > 30) ||
            res.status === "CANCELED" ||
            res.status === "EXPIRED" ||
            res.loanStatus === "PAID_OFF")
        ) {
          setActiveStep(10)
          clearInterval(loanStatusInterval.current)
        }

        if (
          !skipSetActiveState &&
          isGuarantor &&
          (res.status === "CANCELED" ||
            res.status === "EXPIRED" ||
            res.loanStatus === "PAID_OFF" ||
            (res.status === "REJECTED" && Difference_In_Days > 30))
        ) {
          setActiveStep(0)
          clearInterval(loanStatusInterval.current)
        }

        if (
          res.status === "REJECTED" &&
          Difference_In_Days < 30 &&
          !isGuarantor &&
          JSON.parse(res.applicationState).activeStep !== 10 &&
          JSON.parse(res.applicationState).activeStep !== 9
        ) {
          setAppRejected(true)
          clearInterval(loanStatusInterval.current)
        }
      }
    }, 1000)
    return true
  }

  const getLoanApplication = async skipSetActiveState => {
    let loanRequest

    const [loans, loansStatus] = await REST({
      url: `${
        isGuarantor
          ? API_MAP.getGuarantorApplications
          : API_MAP.getLoanApplications
      }`,
      method: "GET",
    })

    if (loansStatus === 400 || loansStatus === 401 || loansStatus === 403) {
      setStepLoading(false)

      return false
    }

    if (loans && loans[0] && loans[0].businessId) {
      localStorage.setItem("businessId", loans[0].businessId)
    }

    if (loans && loans[0] && loans[0].purpose) {
      localStorage.setItem("moneyReason", loans[0].purpose)
    }

    loanRequest = await REST({
      url: isGuarantor
        ? `${domain}/origination/api/guarantorApplication/${
            loans && loans[0] && loans[0].businessId
          }`
        : API_MAP.loanApplication,
      method: "GET",
    })

    const [res, status] = loanRequest

    setAppLoading(false)

    if (status === 200) {
      const Difference_In_Time =
        new Date().getTime() - new Date(res.lastStatusUpdate).getTime()

      const Difference_In_Days = Difference_In_Time / (1000 * 3600 * 24)

      if (activeStep === 0) {
        localStorage.setItem("Authenticated", true)
      }

      // res.status = "APPROVED"

      // res.loanStatus = "FUNDED"

      setLoanApplication(res)
      if (res.applicationState) {
        const applicationState = JSON.parse(res.applicationState)
        setAppLoading(true)
        setIsNotFirstApplication(!applicationState.isFirstApplication)

        if (
          res.status !== "REJECTED" &&
          res.status !== "CANCELED" &&
          res.status !== "EXPIRED" &&
          res.loanStatus !== "PAID_OFF"
        ) {
          if (isGuarantor) {
            if (applicationState.activeStep === 6) {
              applicationState.state[6] = res
            }
          } else {
            if (applicationState.activeStep === 5) {
              applicationState.state[5] = res.loanOffers
            }
          }
        }

        setFormState(applicationState.state)

        setActiveStep(applicationState.activeStep)

        /**
         * Send to summary if not guarantor and rejected or canceled
         */
        if (
          !isGuarantor &&
          (res.status === "REJECTED" ||
            res.status === "CANCELED" ||
            res.status === "EXPIRED" ||
            res.loanStatus === "PAID_OFF")
        ) {
          setActiveStep(9)
        }

        if (
          isGuarantor &&
          ((res.status === "REJECTED" && Difference_In_Days < 30) ||
            (skipSetActiveState &&
              res.status === "REJECTED" &&
              Difference_In_Days > 30) ||
            res.status === "CANCELED" ||
            res.status === "EXPIRED" ||
            res.loanStatus === "PAID_OFF")
        ) {
          setActiveStep(10)
        }

        if (
          !skipSetActiveState &&
          isGuarantor &&
          (res.status === "CANCELED" ||
            res.status === "EXPIRED" ||
            res.loanStatus === "PAID_OFF" ||
            (res.status === "REJECTED" && Difference_In_Days > 30))
        ) {
          setActiveStep(0)
        }

        setAppLoading(false)
      } else {
        if (localStorage.getItem("Authenticated")) {
          setActiveStep(1)
        }
      }

      if (res.status === "SUBMITTED") {
        await checkLoanStatus(skipSetActiveState)
      }

      if (
        res.status === "REJECTED" &&
        Difference_In_Days < 30 &&
        !isGuarantor &&
        JSON.parse(res.applicationState).activeStep !== 10 &&
        JSON.parse(res.applicationState).activeStep !== 9
      ) {
        setAppRejected(true)
      }
    }

    if (status === 400 || status === 401 || status === 403) {
      setStepLoading(false)

      return false
    }

    return res
  }

  const getTermsModalContent = async () => {
    const body =
      showTermsModal === "marketing"
        ? {
            name: "MARKETING_CONSENT",
            description: "Marketing consent",
          }
        : showTermsModal === "terms"
        ? {
            name: "TERMS_AND_CONDITIONS",
            description: "Terms and Conditions",
          }
        : showTermsModal === "privacy"
        ? {
            name: "PRIVACY_POLICY",
            description: "Privacy Policy",
          }
        : showTermsModal === "esign"
        ? {
            name: "ESIGN_CONSENT",
            description: "E-Sign Consent",
          }
        : showTermsModal === "credit"
        ? {
            name: "CREDIT_PROFILE_AUTHORIZATION",
            description: "Credit Profile Authorization",
          }
        : showTermsModal === "borrower"
        ? {
            name: "BORROWER_AGREEMENT",
            description: "Borrower Agreement",
          }
        : showTermsModal === "guarantor"
        ? {
            name: "GUARANTOR_AGREEMENT",
            description: "Guarantor Agreement",
          }
        : showTermsModal === "paymentAuthorization"
        ? {
            name: "AUTOMATIC_PAYMENTS_AUTHORIZATION",
            description: "Payment Authorization",
          }
        : showTermsModal === "paymentAuthorizationGuarantor"
        ? {
            name: "AUTOMATIC_PAYMENTS_AUTHORIZATION_GUARANTOR",
            description: "Payment Authorization",
          }
        : null

    if (body) {
      const [res, status] = await REST({
        url: API_MAP.getAgreement,
        method: "POST",
        body: body,
      })

      if (status === 200 && res) {
        setTermsModalContent(res)
      }
    }
  }

  const submitStep = async (url, body) => {
    document.body.scrollTop = 0
    document.documentElement.scrollTop = 0

    setStepLoading(true)

    if (body) {
      const req = await REST({
        url: url,
        method: "POST",
        body: body,
      })

      setStepLoading(false)

      return req
    }
  }

  const updateLoanApplication = async (id, body) =>
    await REST({
      url: `${domain}/origination/api/loanApplication/${id}`,
      method: "POST",
      body: body,
    })

  const updateApplicationState = async body => {
    document.body.scrollTop = 0
    document.documentElement.scrollTop = 0

    setStepLoading(true)

    if (body) {
      let updateRequest = await REST({
        url: isGuarantor
          ? API_MAP.updateGuarantorApplicationState
          : API_MAP.updateLoanApplicationState,
        method: "POST",
        body: body,
      })

      const [res, status] = updateRequest

      let loan = await getLoanApplication()

      setStepLoading(false)

      if (status === 200 && loan && loan.status !== "REJECTED") {
        setActiveStep(activeStep + 1)
      }
    }
  }

  const submitStepAndUpdateAppState = async (url, body, resumeApp) => {
    if (activeStep >= 6) {
      let loan = await getLoanApplication()

      if (!loan) {
        return false
      }
    }

    if (url) {
      const req = await submitStep(url, body)

      if (
        req &&
        req[1] &&
        (req[1] === 400 || req[1] === 401 || req[1] === 403 || req[1] === 500)
      ) {
        if (
          req[0].message ===
            "Application cannot be updated.Application status is:CANCELED. Cancel the application and create a new one if you want to change information!" ||
          req[0].message ===
            "Application cannot be updated.Application status is:REJECTED. Cancel the application and create a new one if you want to change information!" ||
          req[0].message ===
            "Application cannot be updated.Application status is:EXPIRED. Cancel the application and create a new one if you want to change information!"
        ) {
          if (!isGuarantor) {
            setActiveStep(9)
          } else {
            setActiveStep(10)
          }
        }

        return req
      }
    }

    const formStateClone = produce(formState, draft => {
      draft[activeStep] = body
    })

    setFormState(formStateClone)

    await updateApplicationState({
      applicationState: JSON.stringify({
        state: formStateClone,
        activeStep: activeStep + 1,
      }),
    })

    if (resumeApp && isGuarantor && !loggedIn) {
      // window.location = `${window.location.href}?resumeApp=true`
      navigate(`${window.location.pathname}?resumeApp=true`, {
        replace: true,
      })
    }
  }

  return (
    <ApplyContext.Provider
      value={{
        API_MAP,
        showTermsModal,
        setShowTermsModal,
        termsModalContent,
        setTermsModalContent,
        appLoading,
        setAppLoading,
        appLoadingStatus,
        setAppLoadingStatus,
        appRejected,
        setAppRejected,
        stepLoading,
        setStepLoading,
        activeStep,
        setActiveStep,
        formState,
        setFormState,
        loanApplication,
        checkLoanStatus,
        getLoanApplication,
        getTermsModalContent,
        submitStep,
        submitStepAndUpdateAppState,
        linkManually,
        setLinkManually,
        linkBankLoading,
        setLinkBankLoading,
        selectedPaymentType,
        setSelectedPaymentType,
        repaymentTypes,
        setRepaymentTypes,
        toggleBankAccountUpdate,
        setToggleBankAccountUpdate,
        todoItems,
        setTodoItems,
        getTodoItems,
        initiateNewLoanApplication,
        updateLoanApplication,
        getRepaymentTypes,
        addRepaymentMethod,
        updateBankAccount,
        updateRepaymentMethod,
        makePayment,
        makePublicPayment,
        makePlaidPayment,
        getPaymentLink,
        getPaymentDetails,
        addDisbursementAccount,
        isNotFirstApplication,
        loggedIn,
        setLoggedIn,
      }}
    >
      {children}
    </ApplyContext.Provider>
  )
}

export default ApplyContext
